/**
 * A page arriving under the workspace nav.
 *
 * "Every surface opens from what launched it" (`docs/SCHEDULE_MOTION_DESIGN.md`),
 * at page scale: a page opened from a nav chip, a view or an app-menu row
 * grows out of that control — a fade with a scale from .985 about the
 * control's centre, clamped into the page — and a page reached any other way
 * rises 8px into place. Page load-in is structure, so `--ease` at
 * `--motion-page` (300ms), never a spring.
 *
 * NEVER TWO LAYERS OF TEXT. The router destroys the old page as it creates the
 * new one, so the outgoing text is gone before the first frame of this; only
 * one page is ever on screen.
 *
 * NO RESIDUE. A WAAPI animation with the kit's `backwards` fill: once it ends
 * the page carries no transform. (The CSS page-enter it replaces ended on a
 * transform that its `both` fill kept for good, which made every page a
 * containing block for its `position: fixed` children.)
 *
 * REDUCED MOTION. Nothing plays; the page is simply there.
 */
import {
  isReducedMotion,
  motionTokens,
  originFrom,
  play,
  resolveMotion,
  type MotionHandle,
  type NamedMotion,
} from '../../../../core/motion';

/** The doctrine row: page / route / canvas load-in. */
export const ROUTE_ENTER: NamedMotion = {
  duration: 'page',
  curve: 'ease',
  doctrine: 'Page / route / canvas load-in — --ease, 300ms (--motion-page)',
};

/** How far a page is from full size when it starts growing out of its launcher. */
export const ROUTE_ENTER_SCALE = 0.985;

/** Animate `page` in, from `origin` when a nav control launched it. */
export function playRouteEnter(page: HTMLElement, origin: Element | null): MotionHandle | null {
  if (isReducedMotion()) return null;
  const tokens = motionTokens();
  const timing = { ...resolveMotion(ROUTE_ENTER, tokens), channel: 'route-enter' };
  if (origin) {
    const transformOrigin = originFrom(origin.getBoundingClientRect(), page.getBoundingClientRect());
    return play(page, [
      { opacity: 0, transform: `scale(${ROUTE_ENTER_SCALE})`, transformOrigin },
      { opacity: 1, transform: 'none', transformOrigin },
    ], timing);
  }
  return play(page, [
    { opacity: 0, transform: `translateY(${tokens.reveal.riseSm}px)` },
    { opacity: 1, transform: 'none' },
  ], timing);
}
